import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import i18n from 'simple-react-i18n'
import { Grid } from '@mui/material'
import { StyledFieldSet, StyledLegend } from 'components/StyledElements'
import Checkbox from 'components/forms/Checkbox'
import Select from 'components/forms/Select'
import MultiContributorsAutocomplete from 'referencial/components/contributor/components/MultiContributorsAutocomplete'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { getIntegrationModes } from 'utils/JobUtils'
import { getQualificationSelectOptions, getStatusSelectOptions } from 'utils/StatusUtil'
import { SANDRE } from 'referencial/constants/ReferencialConstants'
import IntegrationModeDialog from 'import/components/content/IntegrationModeDialog'
import HydrometryAction from 'hydrometry/actions/HydrometryAction'
import ReferencialAction from 'referencial/action/ReferencialAction'
import ContributorAction from 'referencial/components/contributor/actions/ContributorAction'
import useSandreList from 'utils/customHook/useSandreList'
import Input from 'components/forms/Input'

const AddHydroStepOptionsMareGraphe = ({
    station,
    changeStation,
    filters,
    changeFilters,
    jobDataTypes,
    setJobDataTypes,
}) => {
    const {
        hydrometryDataTypes,
        sandreCodes,
        contributors,
    } = useSelector(store => ({
        hydrometryDataTypes: store.HydrometryReducer.hydrometryDataTypes,
        sandreCodes: store.ReferencialReducer.sandreCodes,
        contributors: store.ContributorReducer.contributors,
    }), shallowEqual)

    const dispatch = useDispatch()

    useEffect(() => {
        if (!hydrometryDataTypes.length) {
            dispatch(HydrometryAction.fetchHydrometryDataTypes())
        }

        if (!sandreCodes.length) {
            dispatch(ReferencialAction.fetchSandreCodes())
        }

        if (!contributors.length) {
            dispatch(ContributorAction.fetchContributors())
        }
    }, [])

    useEffect(() => {
        if (!filters || filters.stationsMode) {
            return
        }

        changeFilters({
            stationsMode: 'stations',
            stations: station?.code ? [station.code] : [],
        })
    }, [station?.code])

    const integrationModes = getIntegrationModes().map(im => im.value)

    const onChangeDataTypes = (dataType) => {
        const newDataTypes = jobDataTypes.includes(dataType)
            ? jobDataTypes.filter(jdt => jdt !== dataType)
            : [...jobDataTypes, dataType]

        setJobDataTypes(newDataTypes)
    }


    const onChangeDataIntegration = (dataType) => {
        const filteredDataTypes = jobDataTypes.filter(
            jdt => !integrationModes.includes(jdt),
        )

        setJobDataTypes(
            dataType
                ? [...filteredDataTypes, dataType]
                : filteredDataTypes,
        )
        changeFilters({ importMode: dataType })
    }

    const measureNatures = useSandreList(SANDRE.PIEZOMETER_MEASURE_NATURE, true)
    const obtainingModes = useSandreList(SANDRE.OBTAINING_MODE)

    return (
        <>
            <StyledFieldSet>
                <StyledLegend>{i18n.credentials}</StyledLegend>
                <Grid container spacing={2} alignItems='center' sx={{ pb: 2 }}>
                    <Grid size={6}>
                        <Input
                            value={station?.code ?? ''}
                            onChange={v => changeStation({ code: v })}
                            title={i18n.tidegaugeCode}
                            placeholder={i18n.tidegaugeExample}
                        />
                    </Grid>
                </Grid>
            </StyledFieldSet>
            <StyledFieldSet>
                <StyledLegend>{i18n.options}</StyledLegend>
                <Grid container spacing={2}>
                    <Grid size={6}>
                        <Select
                            value={jobDataTypes.find(d => integrationModes.includes(d))}
                            options={getIntegrationModes()}
                            label={i18n.dataIntegration}
                            labelSpan={<IntegrationModeDialog />}
                            onChange={onChangeDataIntegration}
                        />
                    </Grid>
                    <Grid size={3}>
                        <Checkbox
                            checked={jobDataTypes.includes('stationIntegration')}
                            label={i18n.integrateStationInfo}
                            onChange={() => onChangeDataTypes('stationIntegration')}
                        />
                    </Grid>
                    <Grid size={3}>
                        <Checkbox
                            checked={jobDataTypes.includes('withEvents')}
                            label={i18n.importEvents}
                            onChange={() => onChangeDataTypes('withEvents')}
                        />
                    </Grid>
                </Grid>
            </StyledFieldSet>
            <StyledFieldSet>
                <StyledLegend>{i18n.defaultValue}</StyledLegend>
                <Grid container spacing={2}>
                    <Grid size={3}>
                        <Select
                            label={i18n.status}
                            value={filters.status}
                            options={getStatusSelectOptions()}
                            onChange={v => changeFilters({ status: v })}
                        />
                    </Grid>
                    <Grid size={3}>
                        <Select
                            label={i18n.qualification}
                            value={filters.qualification}
                            options={getQualificationSelectOptions()}
                            onChange={v => changeFilters({ qualification: v })}
                        />
                    </Grid>
                    <Grid size={6}>
                        <MultiContributorsAutocomplete
                            label={i18n.producer}
                            values={filters.producer}
                            options={contributors ?? []}
                            onChange={v => changeFilters({ producer: v })}
                        />
                    </Grid>
                    <Grid size={3}>
                        <Select
                            label={i18n.measureNature}
                            value={filters.nature}
                            options={measureNatures}
                            onChange={v => changeFilters({ nature: v })}
                        />
                    </Grid>
                    <Grid size={3}>
                        <Select
                            label={i18n.obtainningMode}
                            value={filters.obtainingMode}
                            options={obtainingModes}
                            onChange={v => changeFilters({ obtainingMode: v })}
                        />
                    </Grid>
                    <Grid size={3}>
                        <MultiContributorsAutocomplete
                            label={i18n.administrator}
                            values={filters.manager}
                            options={contributors ?? []}
                            onChange={v => changeFilters({ manager: v })}
                        />
                    </Grid>
                    <Grid size={3}>
                        <MultiContributorsAutocomplete
                            label={i18n.validator}
                            values={filters.validator}
                            options={contributors ?? []}
                            onChange={v => changeFilters({ validator: v })}
                        />
                    </Grid>
                </Grid>
            </StyledFieldSet>
            <StyledFieldSet>
                <StyledLegend>{i18n.dataTypes}</StyledLegend>
                <Grid container spacing={2}>
                    <Grid>
                        <Checkbox
                            checked={filters.height}
                            label={i18n.height}
                            onChange={v => changeFilters({ height: v })}
                        />
                    </Grid>
                    <Grid>
                        <Checkbox
                            checked={!!filters.forecastTide}
                            label={i18n.forecastTide}
                            onChange={v => changeFilters({ forecastTide: v })}
                        />
                    </Grid>
                    <Grid>
                        <Checkbox
                            checked={!!filters.surcharge}
                            label={i18n.surcharge}
                            onChange={v => changeFilters({ surcharge: v })}
                        />
                    </Grid>
                    <Grid>
                        <Checkbox
                            checked={!!filters.surchargeandforecast}
                            label={i18n.surchargeandforecast}
                            onChange={v => changeFilters({ surchargeandforecast: v })}
                        />
                    </Grid>
                    <Grid>
                        <Checkbox
                            checked={!!filters.surchargeReal}
                            label={i18n.surchargeReal}
                            onChange={v => changeFilters({ surchargeReal: v })}
                        />
                    </Grid>
                    <Grid>
                        <Checkbox
                            checked={!!filters.tide}
                            label={i18n.tide}
                            onChange={v => changeFilters({ tide: v })}
                        />
                    </Grid>
                </Grid>
                <Grid container spacing={2} mt={2}>
                    <Grid size={4}>
                        <Select
                            label={i18n.height}
                            value={filters.heightDt}
                            options={[{ id: 4, label: i18n.height }]}
                            onChange={v => changeFilters({ heightDt: v })}
                        />
                    </Grid>
                    {['forecastTide', 'surcharge', 'surchargeandforecast', 'surchargeReal'].map(type => (
                        <Grid size={4} key={type}>
                            <Select
                                label={i18n[type]}
                                value={filters[`${type}Dt`]}
                                options={hydrometryDataTypes ?? []}
                                onChange={v => changeFilters({ [`${type}Dt`]: v })}
                            />
                        </Grid>
                    ))}
                </Grid>
            </StyledFieldSet>
        </>
    )
}

AddHydroStepOptionsMareGraphe.propTypes = {
    station: PropTypes.object,
    changeStation: PropTypes.func,
    filters: PropTypes.object,
    changeFilters: PropTypes.func,
    jobDataTypes: PropTypes.arrayOf(PropTypes.string),
    setJobDataTypes: PropTypes.func,
}

export default AddHydroStepOptionsMareGraphe